import { api } from "@/shared/api/instance";
import type { PaginatedResponse } from "@/shared/types/paginatedResponse";
import type {
  Order,
  SoloOrder,
  Draft,
  CreateOrderPayload,
  CreateDraftPayload,
} from "../types/order.types";
import type { ManualFlightsQuery } from "@/features/manual-flights/types/manual-flight.types";

export const getOrders = async (
  query: ManualFlightsQuery = {}
): Promise<PaginatedResponse<Order>> => {
  const response = await api.get<PaginatedResponse<Order>>("/api/v1/order", {
    params: query,
  });
  return response.data;
};

export const getFilters = async (): Promise<any> => {
  const response = await api.get<any>("/api/v1/dictionary");
  return response.data;
};

export const getOrderById = async (id: string): Promise<SoloOrder> => {
  const res = await api.get<SoloOrder>(
    `/api/v1/order/${encodeURIComponent(id)}`
  );
  return res.data;
};

export const downloadOrderTicket = async (id: string): Promise<void> => {
  const res = await api.get(`/api/v1/order/${encodeURIComponent(id)}/ticket`, {
    responseType: "blob",
  });

  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: "application/pdf" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `ticket-${id}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const addOrderComment = async (
  id: string,
  message: string
): Promise<void> => {
  await api.post(`/api/v1/order/${encodeURIComponent(id)}/comment`, {
    message,
  });
};
export const comfirmPaymentStatus = async (id: string): Promise<void> => {
  const response = await api.post(`/api/v1/order/${id}/payment/confirm`);
  return response.data;
};

const getFilenameFromDisposition = (cd?: string) => {
  if (!cd) return null;

  // content-disposition: attachment; filename="file.csv"
  const m = /filename\*?=(?:UTF-8''|")?([^;"\n]+)"?/i.exec(cd);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
};

interface UpdateFlightDatetimePayload {
  departureAt: string;
  arrivalAt: string;
  orderIds: number[];
}

export const updateFlightDatetime = async (
  payload: UpdateFlightDatetimePayload
): Promise<void> => {
  await api.patch("/api/v1/order/flight-datetime", payload);
};

export const createOrder = async (
  payload: CreateOrderPayload
): Promise<{ orderId: number }> => {
  const response = await api.post<{ orderId: number }>("/api/v1/order", payload);
  return response.data;
};

export const createDraft = async (payload: CreateDraftPayload): Promise<void> => {
  await api.post("/api/v1/manual-flights/template/draft", payload);
};

export const getDrafts = async (
  page: number,
  pageSize: number
): Promise<PaginatedResponse<Draft>> => {
  const response = await api.get<PaginatedResponse<Draft>>(
    "/api/v1/manual-flights/template/draft",
    { params: { page, pageSize } }
  );
  return response.data;
};

export const deleteDraft = async (id: number): Promise<void> => {
  await api.delete(`/api/v1/manual-flights/template/draft/${id}`);
};

export const exportFlights = async () => {
  const res = await api.get("/api/v1/order/export", {
    responseType: "blob",
    headers: {
      Accept: "text/csv", // просим CSV [web:199]
    },
  });

  const cd = res.headers["content-disposition"];
  const filename =
    getFilenameFromDisposition(cd) ?? "flights-export.csv";

  // на всякий случай приводим к правильному mime
  const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" }); // charset опционален [web:199]

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
